import { createApp } from 'vue';
import App from './App.vue';
import './registerServiceWorker';
import router from './router';
import store from './store';
import i18n, { setI18nLocale } from './api/i18n';
import apiClient from './api/index';
import mainPlugins from '@/plugins/mainPlugins.js';
import VueSweetalert2 from 'vue-sweetalert2';
import Swal from 'sweetalert2';
import { onHttpError } from '@/utils/http-error-events';
import { setLoading } from '@/plugins/loading'; // <-- add this
import 'sweetalert2/dist/sweetalert2.min.css';
const app = createApp(App);
const t = i18n.global.t;

let isAuthErrorDialogOpen = false;

const handleHttpError = async ({ status, message }) => {
    if (status === 401) {
        if (isAuthErrorDialogOpen) {
            return;
        }

        isAuthErrorDialogOpen = true;
        store.dispatch('auth/signout');

        const result = await Swal.fire({
            title: t('django.system_msg__status_code_401'),
            text:
                message || 'Authentication has expired. Please sign in again.',
            icon: 'error',
            confirmButtonText: 'OK',
        });

        if (result.isConfirmed && router.currentRoute.value.path !== '/') {
            await router.push('/');
        }

        isAuthErrorDialogOpen = false;
        return;
    }

    if (status === 403) {
        await Swal.fire({
            title: t('django.system_msg__status_code_403'),
            text: 'You do not have permission to access this page',
            icon: 'error',
            confirmButtonText: 'OK',
        });

        if (router.currentRoute.value.path !== '/') {
            await router.push('/');
        }
        return;
    }

    if (status === 500) {
        await Swal.fire({
            title: 'Internal server error',
            text: 'Please try again later',
            icon: 'error',
            confirmButtonText: 'OK',
        });
        return;
    }

    await Swal.fire({
        title: 'Network error',
        text: 'Unable to reach the server, please try again later.',
        icon: 'error',
        confirmButtonText: 'OK',
    });
};

const loadInitialLanguage = async locale => {
    const response = await apiClient.get(
        `/languages/lang/${locale}?disable_cache=false`,
        { skipAuthorization: true }
    );
    i18n.global.setLocaleMessage(locale, response.data);
    i18n.global.locale.value = locale;
    setI18nLocale(locale, response.data);
    store.commit('i18n/setLocale', { locale, messages: response.data });
};

const initializeApp = async () => {
    const defaultLocale = localStorage.getItem('locale') || 'en';
    try {
        await loadInitialLanguage(defaultLocale);
    } catch (e) {
        console.error(
            'Failed to load initial language, proceeding with fallback.',
            e
        );
    }

    onHttpError(handleHttpError); // <-- add this to use the HTTP error handler

    router.beforeEach(async (to, from, next) => {
        setLoading(true);

        const locale = store.getters['i18n/locale'];
        if (!store.getters['i18n/messages'][locale]) {
            await loadInitialLanguage(locale);
        }

        next();
    });

    router.afterEach(() => {
        setLoading(false);
    });

    app.use(mainPlugins);
    app.use(store);
    app.use(router);
    app.use(i18n);

    app.use(VueSweetalert2);

    app.mount('#app');
};

initializeApp();
